import { useCallback, useEffect, useRef, useState } from 'react';
import { humanizeApiError } from '@lapo/core';
import type { ToastInput } from '@lapo/ui';
import type { ApiClient, TodoistTaskDTO, TodoistTodayDTO } from './auth/apiClient.js';
import type { AuthSession } from './auth/provider.js';
import { API_URL } from './config.js';
import { panelCacheKey, readPanelCache, writePanelCache } from './panelCache.js';

/**
 * Owns the Todoist "Today" panel — the content of the right-hand drawer's Today view: the overdue/today
 * task lists, the load/refresh lifecycle, the reconnect-needed flag, and the deferred (5s Undo window)
 * task completion. Behavior-preserving extraction from App.tsx: it paints from the last-known panel
 * cache, refreshes when the Today panel becomes visible, coalesces overlapping loads into one request,
 * and completes tasks optimistically behind a reversible undo bar.
 *
 * Deps are passed in (never reached out of App): `api`, the auth `session` (for uid + gating),
 * `pushToast`, `todoistPanelVisible` (App computes `rightOpen && !!todoist && RIGHT_VIEWS[rightViewIndex]
 * ?.id === 'todoist'` and passes it, reproducing the inline effect's exact gate), `onConnectTodoist`
 * (the OAuth link flow, run by the panel's reconnect button) and `onOpenSettings` (the panel's connect
 * button opens Settings). `session` null (signed out) keeps everything empty/local. `loadTodoistToday`
 * is returned so the assistant's add_task result can refresh the list after creating a task.
 */

/** Client-local YYYY-MM-DD for a Date, so "today"/"overdue" honor the viewer's timezone (not the server's). */
function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export interface UseTodoistTodayDeps {
  api: ApiClient;
  session: AuthSession | null;
  pushToast: (t: ToastInput) => number;
  /** True when the Today panel is the visible view in the open right drawer AND Todoist is connected. */
  todoistPanelVisible: boolean;
  /** Re-run the Todoist OAuth link (same flow as connecting) — driven by the panel's reconnect button. */
  onConnectTodoist: () => Promise<void>;
  /** Open the Settings dialog — the panel's connect button when Todoist isn't linked yet. */
  onOpenSettings: () => void;
}

export interface UseTodoistToday {
  // State.
  todoistToday: TodoistTodayDTO | null;
  todoistLoading: boolean;
  todoistError: string | null;
  todoistReconnect: boolean;
  completingTasks: ReadonlySet<string>;
  /** The task awaiting the 5s undo window (null when none is pending). */
  pendingUndo: TodoistTaskDTO | null;
  // Loader.
  loadTodoistToday: () => Promise<void>;
  // Actions (stable — TodoistPanel is memoized).
  onTodoistRefresh: () => void;
  onTodoistConnect: () => void;
  onTodoistReconnect: () => Promise<void>;
  onCompleteTask: (task: TodoistTaskDTO) => void;
  onUndoComplete: () => void;
  onDismissUndo: () => void;
}

export function useTodoistToday({
  api,
  session,
  pushToast,
  todoistPanelVisible,
  onConnectTodoist,
  onOpenSettings,
}: UseTodoistTodayDeps): UseTodoistToday {
  // Todoist "Today" — the content of the right-hand drawer (`rightOpen`). Loaded when the drawer
  // opens (and on demand refresh); completion is optimistic (removed immediately, reloaded on error).
  const [todoistToday, setTodoistToday] = useState<TodoistTodayDTO | null>(null);
  const [todoistLoading, setTodoistLoading] = useState(false);
  const [todoistError, setTodoistError] = useState<string | null>(null);
  // The stored Todoist token is revoked/invalid (a read/complete returned needsReconnect) — the panel
  // shows a one-tap reconnect instead of an empty list or a raw error.
  const [todoistReconnect, setTodoistReconnect] = useState(false);
  const [completingTasks, setCompletingTasks] = useState<ReadonlySet<string>>(() => new Set());
  // Coalesce burst triggers (drawer open + tab-switch + the visibility refresh can all fire together)
  // into a single request — overlapping full syncs are exactly what hammered Todoist before.
  const todoistInFlightRef = useRef(false);

  // Track the live session in a ref so the async loader can gate on the current identity without
  // re-creating on every session change (mirrors App's sessionRef guard).
  const sessionRef = useRef<AuthSession | null>(session);
  sessionRef.current = session;

  const loadTodoistToday = useCallback(async () => {
    if (!API_URL || !sessionRef.current) return;
    if (todoistInFlightRef.current) return; // a load is already running — let it finish, don't stack
    todoistInFlightRef.current = true;
    setTodoistLoading(true);
    setTodoistError(null);
    // Client-local YYYY-MM-DD so "today" and "overdue" honor the user's timezone, not the server's.
    const dateKey = localDateKey(new Date());
    try {
      const view = await api.getTodoistToday(dateKey);
      setTodoistToday(view);
      setTodoistReconnect(!!view.needsReconnect);
      setTodoistError(view.error ?? null);
    } catch (err) {
      setTodoistError(humanizeApiError(err));
    } finally {
      setTodoistLoading(false);
      todoistInFlightRef.current = false;
    }
  }, [api]);

  // Hydrate Today from the last-known cache so the panel paints instantly; the visible-refresh below
  // then updates it in the background.
  useEffect(() => {
    if (!session) return;
    const cached = readPanelCache<TodoistTodayDTO>(panelCacheKey('todoist-today', session.user.uid));
    if (cached) setTodoistToday(cached);
  }, [session]);

  // Persist Today (on load + after optimistic completion) for the next hydrate.
  useEffect(() => {
    if (session && todoistToday) writePanelCache(panelCacheKey('todoist-today', session.user.uid), todoistToday);
  }, [session, todoistToday]);

  // Refresh whenever the Today panel becomes visible (drawer open, Todoist connected, that panel
  // active — including after cycling to it) so it reflects current state.
  useEffect(() => {
    if (todoistPanelVisible) void loadTodoistToday();
  }, [todoistPanelVisible, loadTodoistToday]);

  // Stable Todoist-panel handlers (audit F3): TodoistPanel is memoized — inline arrows would defeat it.
  const onTodoistRefresh = useCallback(() => {
    void loadTodoistToday();
  }, [loadTodoistToday]);
  const onTodoistConnect = useCallback(() => onOpenSettings(), [onOpenSettings]);
  const onTodoistReconnect = useCallback(async () => {
    // Re-run the OAuth link (same flow as connecting), then clear the flag and reload.
    await onConnectTodoist();
    setTodoistReconnect(false);
    void loadTodoistToday();
  }, [onConnectTodoist, loadTodoistToday]);

  // Completing a task is deferred: it strikes out instantly and shows a 5s Undo bar before the server
  // is told, so a mis-tap is trivially reversible. The task awaiting that window lives in `pendingUndo`
  // (and a ref, so the timer/handlers read the latest without re-subscribing).
  const [pendingUndo, setPendingUndo] = useState<TodoistTaskDTO | null>(null);
  const pendingUndoRef = useRef<TodoistTaskDTO | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Actually complete on the server, then remove from the list on success (revert + reload on error).
  const commitComplete = useCallback(
    (task: TodoistTaskDTO) => {
      if (undoTimerRef.current) {
        clearTimeout(undoTimerRef.current);
        undoTimerRef.current = null;
      }
      if (pendingUndoRef.current?.taskId === task.taskId) {
        pendingUndoRef.current = null;
        setPendingUndo(null);
      }
      setCompletingTasks((s) => new Set(s).add(task.taskId));
      void (async () => {
        try {
          const result = await api.completeTodoistTask(task.accountId, task.taskId);
          if (result.needsReconnect) {
            // Token died between load and completion — flag a reconnect and reload (which repaints
            // the panel's reconnect prompt) rather than optimistically dropping the task.
            setTodoistReconnect(true);
            void loadTodoistToday();
            return;
          }
          setTodoistToday((cur) =>
            cur
              ? {
                  ...cur,
                  overdue: cur.overdue.filter((t) => t.taskId !== task.taskId),
                  today: cur.today.filter((t) => t.taskId !== task.taskId),
                }
              : cur,
          );
        } catch (err) {
          pushToast({ tone: 'error', title: 'Could not complete task', message: humanizeApiError(err) });
          void loadTodoistToday();
        } finally {
          setCompletingTasks((s) => {
            const next = new Set(s);
            next.delete(task.taskId);
            return next;
          });
        }
      })();
    },
    [api, pushToast, loadTodoistToday],
  );

  // Click "complete": strike it out and open the 5s undo window. A second completion (of a different
  // task) commits the one already pending first, so the single bottom bar always tracks the latest.
  const onCompleteTask = useCallback(
    (task: TodoistTaskDTO) => {
      const prev = pendingUndoRef.current;
      if (prev && prev.taskId !== task.taskId) commitComplete(prev);
      pendingUndoRef.current = task;
      setPendingUndo(task);
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      undoTimerRef.current = setTimeout(() => commitComplete(task), 5000);
    },
    [commitComplete],
  );

  // Undo: cancel the pending completion and un-strike the task (no server call).
  const onUndoComplete = useCallback(() => {
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
    pendingUndoRef.current = null;
    setPendingUndo(null);
  }, []);

  // Dismiss the undo bar: commit the completion immediately rather than waiting out the 5s.
  const onDismissUndo = useCallback(() => {
    const task = pendingUndoRef.current;
    if (task) commitComplete(task);
  }, [commitComplete]);

  // Flush any pending completion timer on unmount (the strike-out is transient UI, not a commit).
  useEffect(() => () => void (undoTimerRef.current && clearTimeout(undoTimerRef.current)), []);

  return {
    todoistToday,
    todoistLoading,
    todoistError,
    todoistReconnect,
    completingTasks,
    pendingUndo,
    loadTodoistToday,
    onTodoistRefresh,
    onTodoistConnect,
    onTodoistReconnect,
    onCompleteTask,
    onUndoComplete,
    onDismissUndo,
  };
}
